import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Box, 
  Modal, 
  LoadingOverlay, 
  Button, 
  Group, 
  Divider,
  Text,
  ActionIcon,
  Code,
  Badge,
  Card
} from '@mantine/core';
import { IconCheck, IconFilter, IconShield, IconX } from '@tabler/icons';
import { CustomQueryBuilder } from './CustomQueryBuilder';
import { useAuth0 } from '@auth0/auth0-react';
import type { Field, RuleGroupType } from 'react-querybuilder';
import { ExternalObjectConf } from '@bageltechnologies/bagel-common';
import { SourceIcon } from '../../../../utils/SourceIcon';


export interface FilterPopupProps {
  opened: boolean;
  onClose: () => void;
  externalObj: ExternalObjectConf;
  integrationKey: string;
  updateExternalObject: (originObject: string, updates: Partial<ExternalObjectConf>) => Promise<void>;
}

export const FilterPopup: React.FC<FilterPopupProps> = ({
  opened,
  onClose,
  externalObj,
  integrationKey,
  updateExternalObject
}) => {
  // const auth0 = useAuth0();
  if (!integrationKey || !externalObj) return null;
  console.log({externalObj});
  // Create a stable default query to avoid re-renders
  const defaultQuery = useCallback(() => ({ 
    combinator: 'and', 
    rules: [] 
  }), []);
  
  // Track if this is the first render with this external object
  const initializedRef = useRef(false);
  
  // Cast to extended type
  const extendedObj = externalObj as ExternalObjectConf;
  
  // Initialize state with the external object's filter or default
  const [localFilterValue, setLocalFilterValue] = useState<RuleGroupType>(
    extendedObj?.filters || defaultQuery()
  );
  const [hasFilterChanges, setHasFilterChanges] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Map fields to the format expected by react-querybuilder
  const queryBuilderFields = React.useMemo(() => {
    if (!extendedObj || !extendedObj.fieldsMeta) {
      return [];
    }
    
    return extendedObj.fieldsMeta.map((field: any) => {
      let valueEditorType = 'text';
      if (field.type === 'boolean') {
        valueEditorType = 'checkbox';
      } else if (field.type === 'number') {
        valueEditorType = 'number';
      } else if (field.type === 'date') {
        valueEditorType = 'date';
      }
      
      let operators = undefined;
      if (field.type === 'boolean') {
        operators = [
          { name: '=', label: 'is' }, 
          { name: '!=', label: 'is not' }
        ];
      }
      
      return {
        name: field.fieldPath || field.name || field.key,
        label: field.label || field.name || field.key,
        valueEditorType,
        operators
      };
    }) as Field[];
  }, [extendedObj]);
  
  console.log({extendedObj, queryBuilderFields});
  
  // Reset state when external object changes (instead of using useEffect)
  if (extendedObj && !initializedRef.current) {
    initializedRef.current = true;
    setLocalFilterValue(extendedObj.filters || defaultQuery());
    setHasFilterChanges(false);
  }
  
  // Reset the initialization flag when the modal closes
  useEffect(() => {
    if (!opened) {
      initializedRef.current = false;
    }
  }, [opened]);

  // Use memoized handler to improve stability
  const handleLocalFilterChange = useCallback((query: RuleGroupType) => {
    console.log("Filter changed:", query);
    setLocalFilterValue(query);
    setHasFilterChanges(true);
  }, []);

  const handleSave = async () => {
    if (!externalObj) return;
    
    try {
      // Keep filter as is - don't convert to undefined
      const filterValue = localFilterValue || defaultQuery();
      
      setIsLoading(true);
      
      // Create updated object with the new filter
      const updatedObject = {
        ...externalObj,
        filter: filterValue
      } as ExternalObjectConf;
      
      // Call the parent callback to save the mapping with the updated object
      if (updateExternalObject) {
        //@ts-ignore
        await updateExternalObject(updatedObject.originObject, { filters: filterValue });
      }
      
      setHasFilterChanges(false);
      
      // Close the modal
      // onClose();
    } catch (error) {
      console.error('Failed to save filter:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Use the default onClose handler provided by props
  const handleModalClose = () => {
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={handleModalClose}
      centered
      withCloseButton={false}
      size="lg"
      padding={0}
      closeOnClickOutside
      closeOnEscape
    >
      <Group p="xs" position='apart'>
        <Group spacing={8}>
          <IconShield size={18} stroke={1.5}/>
          <Text fw={500}>Filter guard</Text>
        </Group>

        
        <ActionIcon onClick={handleModalClose}>
          <IconX size={16} />
        </ActionIcon>
      </Group>
      <Divider />
      <LoadingOverlay visible={isLoading} overlayBlur={2} />

      <Box p={0}>
        <Box p="xs" py={0} mb="xs">
          <Group spacing={8} my="xs">
            <Text fw={500}>Filter records from</Text>

            <Card withBorder p={0} px={8}>
              <Group spacing={6}>
                <SourceIcon sourceName={integrationKey} width={16} height={16} />
                <Text>{externalObj?.label || 'Object'}</Text>
              </Group>
            </Card>
          </Group>
          {queryBuilderFields.length > 0 && (
            <CustomQueryBuilder
              fields={queryBuilderFields}
              query={localFilterValue || defaultQuery()}
              onQueryChange={handleLocalFilterChange}
              addRuleToNewGroups={false}
              autoSelectField={true}
              autoSelectOperator={true}
              showCombinatorsBetweenRules={false}
              showNotToggle={false}
              showCloneButtons={false}
              resetOnFieldChange={true}
              resetOnOperatorChange={false}
            />
          )}
          {queryBuilderFields.length === 0 && (
            <Text color="dimmed" align="center" py="md">
              No fields available for filtering. Please ensure this object has defined fields.
            </Text>
          )}
        </Box>
        <Divider />
        <Group position="right" p="xs">
          <Button 
            onClick={handleSave}
            disabled={!hasFilterChanges || queryBuilderFields.length === 0}
            leftIcon={<IconCheck size={16} />}
            color='indigo'
            size='xs'
          >
            Save Changes
          </Button>
        </Group>
      </Box>
    </Modal>
  );
};

export default FilterPopup; 